export interface ParsedApiError {
  message: string;
  field?: string;
}

interface ApiErrorPayload {
  message?: unknown;
  field?: unknown;
}

function parseJsonMessage(value: string): ParsedApiError | null {
  const trimmed = value.trim();
  if (!trimmed.startsWith('{')) {
    return null;
  }
  try {
    const parsed = JSON.parse(trimmed) as ApiErrorPayload;
    if (typeof parsed.message === 'string' && parsed.message.length > 0) {
      return {
        message: parsed.message,
        field: typeof parsed.field === 'string' ? parsed.field : undefined,
      };
    }
  } catch {
    return null;
  }
  return null;
}

function extractFromPayload(payload: unknown): ParsedApiError | null {
  if (!payload || typeof payload !== 'object') {
    return null;
  }
  const data = payload as ApiErrorPayload;
  if (typeof data.message === 'string' && data.message.length > 0) {
    const fromJson = parseJsonMessage(data.message);
    if (fromJson) {
      return fromJson;
    }
    return {
      message: data.message,
      field: typeof data.field === 'string' ? data.field : undefined,
    };
  }
  if (data.message && typeof data.message === 'object') {
    const nested = data.message as ApiErrorPayload;
    if (typeof nested.message === 'string' && nested.message.length > 0) {
      return {
        message: nested.message,
        field:
          typeof nested.field === 'string'
            ? nested.field
            : typeof data.field === 'string'
              ? data.field
              : undefined,
      };
    }
  }
  return null;
}

/**
 * Normalizes axios/API errors into a user-facing message and optional field key.
 */
export function parseApiError(error: unknown, fallback = 'Une erreur est survenue.'): ParsedApiError {
  const axiosError = error as {
    response?: { data?: ApiErrorPayload };
    message?: string;
  };
  const responseData = axiosError.response?.data;
  const fromResponse = extractFromPayload(responseData);
  if (fromResponse) {
    return fromResponse;
  }
  if (typeof axiosError.message === 'string' && axiosError.message.length > 0) {
    return { message: axiosError.message };
  }
  return { message: fallback };
}
